import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { qualityContent } from '../data/mockData';
import { CheckCircle2, ShieldCheck, Microscope, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import  qualityImage  from '../assets/q.png'

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const Quality = () => {
  const [headerRef, headerVis] = useReveal(0);
  const [cardsRef,  cardsVis]  = useReveal(80);
  const [imgRef,    imgVis]    = useReveal(0);
  const { t } = useTranslation();

  return (
    <section className="ql-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes qlUp   { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes qlPop  { from{opacity:0;transform:scale(0.92) translateY(24px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes qlImg  { from{opacity:0;transform:scaleX(0.9)} to{opacity:1;transform:scaleX(1)} }

        .ql-root {
          font-family: 'Outfit', sans-serif;
          position: relative; overflow: hidden;
        }

        /* ── TOP: teal gradient with centered text ── */
        .ql-top {
          background: linear-gradient(180deg,#e8f5f7 0%,#d4eef2 60%,#c8e9ee 100%);
          padding: 90px 0 80px;
          position: relative; overflow: hidden;
        }
        .ql-top-dots {
          position: absolute; inset: 0; pointer-events: none;
          background-image: radial-gradient(circle, rgba(58,143,163,0.07) 1px, transparent 1px);
          background-size: 36px 36px;
        }
        .ql-top-blob1 {
          position: absolute; width: 480px; height: 480px; border-radius: 50%;
          background: rgba(58,143,163,0.08); top: -160px; right: -80px;
          filter: blur(80px); pointer-events: none;
        }
        .ql-top-blob2 {
          position: absolute; width: 340px; height: 340px; border-radius: 50%;
          background: rgba(90,171,154,0.07); bottom: -80px; left: -60px;
          filter: blur(60px); pointer-events: none;
        }

        .ql-top-inner {
          max-width: 1020px; margin: 0 auto; padding: 0 40px; text-align: center;
          position: relative; z-index: 1;
          opacity: 0; transform: translateY(40px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .ql-top-inner.in { opacity: 1; transform: translateY(0); }

        .ql-eyebrow {
          display: inline-block; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #3a8fa3; background: rgba(58,143,163,0.1);
          border: 1px solid rgba(58,143,163,0.28);
          padding: 5px 18px; border-radius: 999px; margin-bottom: 18px;
        }
        .ql-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4vw, 3.2rem); font-weight: 800;
          color: #1a3a4a; margin: 0 0 18px; line-height: 1.15;
        }
        .ql-desc {
          font-size: 1rem; color: #3d5a6a; line-height: 1.8; margin-bottom: 32px; max-width: 600px; margin-left: auto; margin-right: auto;
        }
        .ql-btn {
          display: inline-flex; align-items: center; gap: 10px;
          background: linear-gradient(135deg, #3a8fa3 0%, #5aab9a 100%);
          color: #fff; font-family: 'Outfit', sans-serif;
          font-size: 0.95rem; font-weight: 500;
          padding: 13px 30px; border-radius: 999px; border: none; cursor: pointer;
          box-shadow: 0 8px 24px rgba(58,143,163,0.32);
          transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
          text-decoration: none; position: relative; overflow: hidden;
        }
        .ql-btn::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(120deg,transparent 30%,rgba(255,255,255,0.3) 50%,transparent 70%);
          background-size: 200% 100%; background-position: 200% center;
          transition: background-position 0.55s ease;
        }
        .ql-btn:hover {
          background: linear-gradient(135deg,#2d7a8e,#4a9888);
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(58,143,163,0.42);
        }
        .ql-btn:hover::after { background-position: -200% center; }

        /* ── CARDS: 2 feature pills floating up from the section ── */
        .ql-cards-zone {
          background: #fff;
          padding: 0 0 80px;
        }
        .ql-cards-inner {
          max-width: 1100px; margin: 0 auto; padding: 0 40px;
        }

        /* Cards pulled up to overlap top section */
        .ql-cards {
          display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
          margin-top: -44px;
          opacity: 0; transform: translateY(32px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        @media(max-width:700px){ .ql-cards{grid-template-columns:1fr;margin-top:-24px;} }
        .ql-cards.in { opacity: 1; transform: translateY(0); }

        .ql-feat-card {
          background: #fff;
          border-radius: 20px; padding: 28px 30px;
          box-shadow: 0 8px 40px rgba(58,143,163,0.12), 0 2px 8px rgba(0,0,0,0.05),
                      inset 0 1px 0 rgba(255,255,255,0.9);
          border: 1px solid rgba(58,143,163,0.1);
          display: flex; align-items: flex-start; gap: 18px;
          transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
          cursor: default; position: relative; overflow: hidden;
        }
        /* Bottom teal shimmer bar */
        .ql-feat-card::after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, #3a8fa3, #5aab9a);
          transform: scaleX(0); transform-origin: left;
          transition: transform 0.4s cubic-bezier(0.4,0,0.2,1);
        }
        .ql-feat-card:hover {
          box-shadow: 0 16px 52px rgba(58,143,163,0.18), 0 4px 14px rgba(0,0,0,0.07);
          border-color: rgba(58,143,163,0.22);
          transform: translateY(-5px);
        }
        .ql-feat-card:hover::after { transform: scaleX(1); }

        .ql-feat-icon-wrap {
          width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
          background: linear-gradient(135deg, rgba(58,143,163,0.1), rgba(90,171,154,0.1));
          border: 1px solid rgba(58,143,163,0.18);
          display: flex; align-items: center; justify-content: center;
          transition: all 0.3s;
        }
        .ql-feat-card:hover .ql-feat-icon-wrap {
          background: linear-gradient(135deg, rgba(58,143,163,0.18), rgba(90,171,154,0.18));
          box-shadow: 0 4px 14px rgba(58,143,163,0.2);
        }
        .ql-feat-icon-wrap svg { color: #3a8fa3; }
        .ql-feat-text { font-size: 0.93rem; color: #3d5a6a; line-height: 1.75; padding-top: 4px; }

        /* ── BOTTOM: image bar ── */
        .ql-img-bar {
          position: relative; overflow: hidden; height: 260px;
          opacity: 0; transition: opacity 0.9s cubic-bezier(0.4,0,0.2,1);
        }
        .ql-img-bar.in { opacity: 1; }
        .ql-img-bar img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform 0.8s cubic-bezier(0.4,0,0.2,1);
        }
        .ql-img-bar:hover img { transform: scale(1.04); }
        .ql-img-bar-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to right,
            rgba(13,34,51,0.65) 0%, rgba(13,34,51,0.2) 40%,
            rgba(13,34,51,0.2) 60%, rgba(13,34,51,0.65) 100%);
        }
        /* Centred label on image */
        .ql-img-bar-label {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 6px;
        }
        .ql-img-bar-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.2rem, 2.5vw, 2rem); font-weight: 800;
          color: #fff; text-shadow: 0 2px 12px rgba(0,0,0,0.4);
        }
        .ql-img-bar-sub {
          font-size: 0.75rem; letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(255,255,255,0.6);
        }
      `}</style>

      {/* TOP CENTERED */}
      <div className="ql-top">
        <div className="ql-top-dots" />
        <div className="ql-top-blob1" /><div className="ql-top-blob2" />
        <div ref={headerRef} className={`ql-top-inner ${headerVis ? 'in' : ''}`}>
          <div className="ql-eyebrow">{t("quality.subtitle")}</div>
          <h2 className="ql-title">{t("quality.title")}</h2>
          <p className="ql-desc">{t("quality.description")}</p>
          <Link to={qualityContent.ctaLink}>
            <button className="ql-btn">{t("quality.cta")}</button>
          </Link>
        </div>
      </div>

      {/* OVERLAPPING FEATURE CARDS */}
      {/* <div className="ql-cards-zone">
        <div className="ql-cards-inner">
          <div ref={cardsRef} className={`ql-cards ${cardsVis ? 'in' : ''}`}>
            {[
              { key: 'quality.feature1', Icon: CheckCircle2 },
              { key: 'quality.feature2', Icon: ShieldCheck },
            ].map(({ key, Icon }, i) => (
              <div key={i} className="ql-feat-card" style={{ transitionDelay: `${i * 90}ms` }}>
                <div className="ql-feat-icon-wrap"><Icon size={22} /></div>
                <p className="ql-feat-text">{t(key)}</p>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* FULL-WIDTH IMAGE BAR */}
      <div ref={imgRef} className={`ql-img-bar ${imgVis ? 'in' : ''}`}>
        <img src={qualityImage} alt="Quality Assurance" />
        <div className="ql-img-bar-overlay" />
        <div className="ql-img-bar-label">
          <div className="ql-img-bar-title">{t("quality.title")}</div>
          <div className="ql-img-bar-sub">{t("quality.subtitle")}</div>
        </div>
      </div>
    </section>
  );
};

export default Quality;